import { createFileRoute, Link } from '@tanstack/react-router';
import PageGray from '../../components/General/PageGray';
import H1 from '../../components/Typography/Headings/H1';
import P from '../../components/Typography/P';
import { Search } from '@testbusters/search';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Chip } from '@testbusters/chip';
import FavouriteQuestionCard from '../../components/FavouriteQuestions/FavouriteQuestionCard';
import Span from '../../components/Typography/Span';
import { useTranslation } from 'react-i18next';
import { useGetFavouriteQuestions, useGetWrongQuestions } from '../../hooks/favouriteQuestions/useFavouriteQuestions';
import Loader from '../../components/Loader/Loader';
import { Button } from '@testbusters/button';
import { cn } from '@/lib/utills';
import { usePagination } from '@/hooks/usePagination';

export const Route = createFileRoute('/_sidebar/mie-domande')({
  component: RouteComponent,
});

type TabId = 'favourites' | 'wrong';

function TabBar({ active, onTabChange }: { active: TabId; onTabChange: (t: TabId) => void }) {
  const { t } = useTranslation();
  const tabRefs = useRef<Partial<Record<TabId, HTMLDivElement | null>>>({});
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const el = tabRefs.current[active];
    if (!el) return;
    setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
    setReady(true);
  }, [active]);

  const tab = (id: TabId, label: string) => (
    <div
      key={id}
      ref={(el) => { tabRefs.current[id] = el; }}
      onClick={() => onTabChange(id)}
      className={cn(
        'cursor-pointer py-3',
        active === id ? 'text-button-primary-primary-default' : 'text-text-body-description',
      )}
    >
      <Span className="font-semibold text-inherit hover:text-button-primary-primary-default">
        {label}
      </Span>
    </div>
  );

  return (
    <div className="-mx-6 lg:-mx-8 px-6 lg:px-8 pt-5 sticky top-[56px] lg:top-0 backdrop-blur-md transition-colors">
      <div className="relative flex gap-7 items-baseline">
        {tab('favourites', t('favouriteQuestions.favourites'))}
        {tab('wrong', t('favouriteQuestions.wrongTab'))}
        {ready && (
          <div
            className="absolute bottom-0 h-0.5 bg-background-accent transition-all duration-200"
            style={{ left: indicator.left, width: indicator.width }}
          />
        )}
      </div>
    </div>
  );
}

function QuestionList({
  questions,
  createTo,
  type,
}: {
  questions: { questionId: string; questionText: string; subjectName: string }[];
  createTo?: string;
  type: 'favourites' | 'wrong';
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);

  const toggleSubject = (subject: string) =>
    setSelectedSubjects((prev) =>
      prev.includes(subject) ? prev.filter((s) => s !== subject) : [...prev, subject],
    );

  const subjects = useMemo(
    () => [...new Set(questions.map((q) => q.subjectName).filter(Boolean))],
    [questions],
  );

  const filtered = useMemo(
    () =>
      questions.filter((q) => {
        const matchesSearch = q.questionText?.toLowerCase().includes(search.toLowerCase());
        const matchesSubject = selectedSubjects.length === 0 || selectedSubjects.includes(q.subjectName ?? '');
        return matchesSearch && matchesSubject;
      }),
    [questions, search, selectedSubjects],
  );

  const { paginatedItems, hasMore, loadMore } = usePagination({ items: filtered, itemsPerPage: 25 });

  if (questions.length === 0) {
    const emptyTitle = type === 'favourites'
      ? t('favouriteQuestions.emptyFavouritesTitle')
      : t('favouriteQuestions.emptyWrongTitle');
    const emptyDesc = type === 'favourites'
      ? t('favouriteQuestions.emptyFavouritesDesc')
      : t('favouriteQuestions.emptyWrongDesc');
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
        <Span className="font-semibold text-lg">{emptyTitle}</Span>
        <P className="text-text-body-description max-w-80">{emptyDesc}</P>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="max-w-200">
        <Search
          showClear
          onClear={() => setSearch('')}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('common.search')}
        />
      </div>
      <div className="max-w-full overflow-hidden">
        <div className="flex items-center gap-4 overflow-x-scroll no-scrollbar">
          {subjects.map((subject) => (
            <Chip
              key={subject}
              onClick={() => toggleSubject(subject as string)}
              className={
                selectedSubjects.includes(subject as string)
                  ? 'bg-white border border-button-primary-primary-default cursor-pointer font-normal'
                  : 'bg-white border border-button-secondary-disabled cursor-pointer font-normal'
              }
            >
              {subject}
            </Chip>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <Span className="text-xs!">{`${filtered.length} ${t('common.questions')}`}</Span>
        {createTo && (
          <Link to={createTo}>
            <Button className="min-h-10" variant="primary">
              {t('exercise.createButton')}
            </Button>
          </Link>
        )}
      </div>
      <div className="flex flex-col gap-5">
        {paginatedItems.map((q) => (
          <FavouriteQuestionCard key={q.questionId} question={q} type={type} />
        ))}
        {hasMore && (
          <button
            onClick={loadMore}
            className="text-primary-500 hover:text-primary-600 font-semibold text-center py-2 transition-colors"
          >
            {t('common.showMore')}
          </button>
        )}
      </div>
    </div>
  );
}

function RouteComponent() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabId>('favourites');
  const { favourites, isFetching: fetchingFavourites } = useGetFavouriteQuestions();
  const { questions: wrongQuestions, isFetching: fetchingWrong } = useGetWrongQuestions();

  const isFetching = activeTab === 'favourites' ? fetchingFavourites : fetchingWrong;

  return (
    <PageGray className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <H1>{t('nav.myQuestions')}</H1>
        <P>{t('favouriteQuestions.subtitle')}</P>
      </div>
      <TabBar active={activeTab} onTabChange={setActiveTab} />
      {isFetching ? (
        <Loader fullScreen />
      ) : activeTab === 'favourites' ? (
        <QuestionList questions={favourites} createTo="/mie-domande/crea" type="favourites" />
      ) : (
        <QuestionList questions={wrongQuestions} createTo="/mie-domande/crea-sbagliate" type="wrong" />
      )}
    </PageGray>
  );
}
